import * as React from 'react';
import {
  type GDSHeadingLevel,
  type GDSIconSource,
  type GDSSpace,
} from '@leagueplatform/genesis-core';
import {
  type MasonryEngineNode,
  type MasonryEngineNodeRendererProps,
} from '@leagueplatform/masonry-engine';
import { SectionHeader } from '@leagueplatform/genesis-core-alpha';
import { MasonryDriverAction } from '#types/masonry-driver-node-actions.ts';
import { formatClassNames } from '../../utils/format-masonry-class-name.util';
import { MasonryTextAction } from '../common/masonry-text-action/masonry-text-action.component';

export interface MasonrySectionHeaderNodeProperties {
  buttonLabel?: string;
  heading: string;
  headingLevel?: GDSHeadingLevel;
  isCtaLabelledByHeading?: boolean;
  paddingTop?: GDSSpace;
  subtitle?: string;
  buttonLabelIcon?: GDSIconSource;
}

export type MasonrySectionHeaderNodeActions = {
  onButtonClicked?: MasonryDriverAction;
};

export type MasonrySectionHeaderNode = MasonryEngineNode<
  'sectionHeader',
  MasonrySectionHeaderNodeProperties,
  MasonrySectionHeaderNodeActions
>;

export type MasonrySectionHeaderRendererProps =
  MasonryEngineNodeRendererProps<MasonrySectionHeaderNode>;

export const MasonrySectionHeaderRenderer = ({
  heading,
  headingLevel,
  buttonLabel,
  buttonLabelIcon,
  subtitle,
  onButtonClicked: onClick,
  isCtaLabelledByHeading,
  paddingTop = '$one',
  rootElementRef,
  className,
  engineClassName,
  locatorId,
  css,
}: MasonrySectionHeaderRendererProps) => {
  const hasTextAction: boolean = !!onClick && !!buttonLabel;

  return (
    <SectionHeader
      css={{ marginBlockStart: paddingTop, ...css }}
      headingLevel={headingLevel}
      isCtaLabelledByHeading={isCtaLabelledByHeading ?? undefined}
      subtitle={subtitle}
      textAction={
        hasTextAction ? (
          <MasonryTextAction
            onClick={onClick}
            text={buttonLabel}
            icon={buttonLabelIcon}
          />
        ) : undefined
      }
      title={heading}
      ref={rootElementRef}
      className={formatClassNames(engineClassName, className)}
      data-testid={locatorId}
    />
  );
};
